import { Fragment, useMemo, useState } from 'react';
import {
  MdCheckCircle,
  MdChevronRight,
  MdClose,
  MdError,
  MdExpandMore,
  MdWarning,
} from 'react-icons/md';
import { RfqExcelImportPreview } from '../../../utils/rfq-excel.types';
import {
  ProcurementActionButton,
  ProcurementPrimaryButton,
} from '../../../components/shared/ProcurementActionButton';
import {
  RFQ_IMPORT_ISSUE_GROUPS,
  buildRfqImportReviewModel,
  cleanImportDisplayValue,
  filterImportReviewRows,
  toImportPartFinderStatus,
  type RfqImportFieldReview,
  type RfqImportFieldStatus,
  type RfqImportPartFinderStatus,
  type RfqImportReviewFilter,
  type RfqImportReviewItemRow,
} from '../../../utils/rfq-import-review';

interface RfqExcelImportModalProps {
  preview: RfqExcelImportPreview;
  fileName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const FLOW_STEPS = [
  'Document',
  'Extracted Data',
  'ERP Master Data',
  'PartFinder',
  'Review',
  'Apply',
];

const FILTERS: Array<{ id: RfqImportReviewFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'matched', label: 'Matched' },
  { id: 'needs_review', label: 'Needs Review' },
];

const FIELD_STATUS_UI: Record<RfqImportFieldStatus, { icon: string; label: string; cls: string }> = {
  MATCHED: { icon: '✓', label: 'Matched', cls: 'text-emerald-700' },
  UNRESOLVED: { icon: '⚠', label: 'Not found', cls: 'text-amber-700' },
  INVALID: { icon: '⚠', label: 'Needs review', cls: 'text-amber-700' },
  EXTRACTED: { icon: '•', label: 'Extracted', cls: 'text-slate-600' },
  NOT_PROVIDED: { icon: '—', label: 'Not provided', cls: 'text-slate-400' },
};

const PF_STATUS_UI: Record<RfqImportPartFinderStatus, { icon: string; label: string; cls: string }> = {
  MATCHED: { icon: '✓', label: 'Matched', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  UNRESOLVED: { icon: '⚠', label: 'No exact match', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  AMBIGUOUS: { icon: '⚠', label: 'Multiple matches', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  NOT_SEARCHED: { icon: '—', label: 'Not searched', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

const PF_STATUS_DETAIL: Record<RfqImportPartFinderStatus, string> = {
  MATCHED: 'PartFinder match',
  UNRESOLVED: 'No exact PartFinder match',
  AMBIGUOUS: 'Multiple PartFinder matches',
  NOT_SEARCHED: 'Not searched — no Part No or IMPA provided',
};

const sectionTitleCls =
  'text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]';

function Empty() {
  return <span className="text-slate-300">—</span>;
}

function cell(value: unknown) {
  return cleanImportDisplayValue(value) ?? <Empty />;
}

function FieldStatus({ field }: { field: RfqImportFieldReview }) {
  const ui = FIELD_STATUS_UI[field.status];
  const showNote =
    field.note &&
    field.status !== 'NOT_PROVIDED' &&
    !(field.status === 'UNRESOLVED' && field.note === 'Not found in ERP master');
  const label =
    field.status === 'EXTRACTED' && !field.isMasterField ? 'Extracted' : ui.label;
  return (
    <div className="min-w-0">
      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${ui.cls}`}>
        <span aria-hidden>{ui.icon}</span>
        {label}
        {field.status === 'MATCHED' && field.erpValue ? (
          <span className="font-normal text-slate-500">as “{field.erpValue}”</span>
        ) : null}
        {field.status === 'EXTRACTED' && field.erpValue ? (
          <span className="font-normal text-slate-500">→ {field.erpValue}</span>
        ) : null}
      </span>
      {showNote ? (
        <p className="mt-0.5 text-[11px] text-[hsl(var(--muted-foreground))]">{field.note}</p>
      ) : null}
    </div>
  );
}

function ItemDetails({ row }: { row: RfqImportReviewItemRow }) {
  const { item } = row;
  const pfStatus = toImportPartFinderStatus(row.resolution?.status);
  const details: Array<[string, string | null]> = [
    ['Drawing No', cleanImportDisplayValue(item.drawingNumber)],
    ['Alt Part No', cleanImportDisplayValue(item.altPartNo)],
    ['Model / Serial', cleanImportDisplayValue(item.modelSerial)],
    ['Dimensions', cleanImportDisplayValue(item.dimensions)],
    ['Quality', cleanImportDisplayValue(item.quality)],
    ['Priority', cleanImportDisplayValue(item.deliveryPriority)],
    ['Expected Delivery', cleanImportDisplayValue(item.expectedDeliveryDate)],
    ['Remarks', cleanImportDisplayValue(item.generalRemark)],
  ];
  const provided = details.filter(([, v]) => v);

  return (
    <div className="grid gap-4 px-4 py-3 sm:grid-cols-2">
      <div>
        <p className={`${sectionTitleCls} mb-2`}>Item details</p>
        {provided.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">No additional details in document.</p>
        ) : (
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
            {provided.map(([label, value]) => (
              <Fragment key={label}>
                <dt className="text-[hsl(var(--muted-foreground))]">{label}</dt>
                <dd className="text-[#0f172a] break-words">{value}</dd>
              </Fragment>
            ))}
          </dl>
        )}
      </div>
      <div>
        <p className={`${sectionTitleCls} mb-2`}>PartFinder</p>
        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
          <dt className="text-[hsl(var(--muted-foreground))]">Part Number</dt>
          <dd className="text-[#0f172a]">{cell(item.partNo)}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">IMPA</dt>
          <dd className="text-[#0f172a]">{cell(item.impaNo)}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">Position</dt>
          <dd className="text-[#0f172a]">{cell(item.positionNo)}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">Status</dt>
          <dd className="text-[#0f172a]">{PF_STATUS_DETAIL[pfStatus]}</dd>
          {row.reasons.length > 0 ? (
            <>
              <dt className="text-[hsl(var(--muted-foreground))]">Reason</dt>
              <dd className="text-[#0f172a]">
                <ul className="space-y-0.5">
                  {row.reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </dd>
            </>
          ) : null}
        </dl>
      </div>
    </div>
  );
}

export function RfqExcelImportModal({
  preview,
  fileName,
  onConfirm,
  onCancel,
}: RfqExcelImportModalProps) {
  const model = useMemo(() => buildRfqImportReviewModel(preview), [preview]);
  const [filter, setFilter] = useState<RfqImportReviewFilter>('all');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const visibleRows = useMemo(() => filterImportReviewRows(model.rows, filter), [model.rows, filter]);
  const itemsNeedingReview = model.rows.filter((r) => r.statusDisplay.needsReview).length;
  const hasIssues = model.issues.length > 0;
  const issueGroups = RFQ_IMPORT_ISSUE_GROUPS.map((g) => ({
    ...g,
    messages: model.issues.filter((i) => i.group === g.id).map((i) => i.message),
  })).filter((g) => g.messages.length > 0);

  const stats: Array<{ label: string; value: number; hint?: string; warn?: boolean }> = [
    { label: 'Items Imported', value: model.counts.itemsImported },
    {
      label: 'ERP Fields Matched',
      value: model.counts.erpFieldsMatched,
      hint: model.counts.erpFieldsChecked ? `of ${model.counts.erpFieldsChecked} provided` : undefined,
    },
    { label: 'Needs Review', value: model.counts.needsReview, warn: model.counts.needsReview > 0 },
    {
      label: 'PartFinder Matches',
      value: model.counts.partFinderMatches,
      hint: model.counts.partFinderChecked ? `of ${model.counts.partFinderChecked} searched` : undefined,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 backdrop-blur-sm sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="rfq-import-review-title"
        className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="border-b border-[#0088cc]/15 px-4 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 id="rfq-import-review-title" className="text-lg font-bold text-[#0f172a]">
                Review RFQ Import
              </h3>
              <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs">
                <div className="flex min-w-0 gap-1.5">
                  <dt className="text-[hsl(var(--muted-foreground))]">Source</dt>
                  <dd className="max-w-[18rem] truncate font-semibold text-[#0f172a]" title={fileName}>
                    {fileName || '—'}
                  </dd>
                </div>
                <div className="flex gap-1.5">
                  <dt className="text-[hsl(var(--muted-foreground))]">Type</dt>
                  <dd className="font-semibold text-[#0f172a]">{model.sourceType}</dd>
                </div>
                <div className="flex gap-1.5">
                  <dt className="text-[hsl(var(--muted-foreground))]">Status</dt>
                  <dd className={`font-semibold ${model.canApply ? 'text-emerald-700' : 'text-red-600'}`}>
                    {model.statusLabel}
                  </dd>
                </div>
                {model.confidenceLabel ? (
                  <div className="flex gap-1.5">
                    <dt className="text-[hsl(var(--muted-foreground))]">Extraction confidence</dt>
                    <dd className="font-semibold text-[#0f172a]">{model.confidenceLabel}</dd>
                  </div>
                ) : null}
              </dl>
            </div>
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close import review"
              className="rounded-lg p-2 text-[#0088cc]/65 transition-colors hover:bg-[#0088cc]/10 hover:text-[hsl(var(--muted-foreground))]"
            >
              <MdClose className="h-5 w-5" />
            </button>
          </div>
          <ol className="mt-3 flex flex-wrap items-center gap-x-1 gap-y-1 text-[11px] font-semibold">
            {FLOW_STEPS.map((step, i) => (
              <li key={step} className="flex items-center gap-1">
                <span
                  className={
                    step === 'Review'
                      ? 'rounded-md bg-[#0088cc]/10 px-1.5 py-0.5 text-[#0088cc]'
                      : 'text-[hsl(var(--muted-foreground))]'
                  }
                >
                  {step}
                </span>
                {i < FLOW_STEPS.length - 1 ? (
                  <MdChevronRight className="h-3.5 w-3.5 text-slate-300" aria-hidden />
                ) : null}
              </li>
            ))}
          </ol>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 space-y-6 overflow-y-auto px-4 py-5 sm:px-6">
          <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-[#0088cc]/15 sm:grid-cols-4">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className={`px-4 py-3 ${i > 0 ? 'sm:border-l' : ''} ${i % 2 === 1 ? 'border-l' : ''} ${
                  i >= 2 ? 'border-t sm:border-t-0' : ''
                } border-[#0088cc]/10`}
              >
                <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  {s.label}
                </p>
                <p className={`mt-0.5 text-xl font-black ${s.warn ? 'text-amber-700' : 'text-[#0f172a]'}`}>
                  {s.value}
                  {s.hint ? (
                    <span className="ml-1.5 text-xs font-medium text-[hsl(var(--muted-foreground))]">
                      {s.hint}
                    </span>
                  ) : null}
                </p>
              </div>
            ))}
          </div>

          {preview.errors.length > 0 && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-red-600">
                <MdError className="h-4 w-4" />
                Errors
              </p>
              <ul className="space-y-1">
                {preview.errors.map((e) => (
                  <li key={e} className="text-sm text-red-700">
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* RFQ Details: extraction vs ERP resolution side by side */}
          <section>
            <p className={`${sectionTitleCls} mb-2`}>RFQ Details</p>
            <div className="overflow-x-auto rounded-xl border border-[#0088cc]/15">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  <tr>
                    <th className="w-40 px-4 py-2">Field</th>
                    <th className="px-4 py-2">Extracted from document</th>
                    <th className="px-4 py-2">ERP master data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {model.fields.map((f) => (
                    <tr key={f.key} className="align-top">
                      <td className="px-4 py-2 text-xs font-semibold text-[hsl(var(--muted-foreground))]">
                        {f.label}
                      </td>
                      <td className="px-4 py-2 text-[#0f172a] break-words">
                        {f.sourceValue ?? (
                          <span className="text-xs italic text-slate-400">Not provided in document</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {f.isMasterField || f.status !== 'NOT_PROVIDED' ? (
                          <FieldStatus field={f} />
                        ) : (
                          <Empty />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Line items */}
          <section>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
              <p className={sectionTitleCls}>
                Line Items · {model.counts.itemsImported}
              </p>
              <div className="inline-flex overflow-hidden rounded-lg border border-[#0088cc]/15">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFilter(f.id)}
                    className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide transition-colors ${
                      filter === f.id
                        ? 'bg-[#0088cc] text-white'
                        : 'bg-white text-[hsl(var(--muted-foreground))] hover:bg-[#0088cc]/5'
                    }`}
                  >
                    {f.label}
                    {f.id === 'needs_review' && itemsNeedingReview > 0 ? ` (${itemsNeedingReview})` : ''}
                  </button>
                ))}
              </div>
            </div>

            {visibleRows.length === 0 ? (
              <p className="rounded-xl border border-dashed border-[#0088cc]/20 py-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
                No items in this filter.
              </p>
            ) : (
              <div className="max-h-[420px] overflow-auto rounded-xl border border-[#0088cc]/15">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    <tr>
                      <th className="w-12 px-3 py-2">#</th>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2">Part No</th>
                      <th className="px-3 py-2">IMPA</th>
                      <th className="px-3 py-2">Position</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2">UOM</th>
                      <th className="px-3 py-2">PartFinder</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visibleRows.map((row) => {
                      const { item, index } = row;
                      const expanded = expandedIndex === index;
                      const pf = PF_STATUS_UI[toImportPartFinderStatus(row.resolution?.status)];
                      const toggle = () => setExpandedIndex(expanded ? null : index);
                      return (
                        <Fragment key={item.id || String(index)}>
                          <tr
                            onClick={toggle}
                            className={`cursor-pointer transition-colors hover:bg-[#0088cc]/5 ${
                              expanded ? 'bg-[#0088cc]/5' : ''
                            }`}
                          >
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                aria-expanded={expanded}
                                aria-label={`${expanded ? 'Hide' : 'Show'} details for item ${index + 1}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggle();
                                }}
                                className="inline-flex items-center gap-0.5 text-xs font-semibold text-[hsl(var(--muted-foreground))]"
                              >
                                {expanded ? (
                                  <MdExpandMore className="h-4 w-4" />
                                ) : (
                                  <MdChevronRight className="h-4 w-4" />
                                )}
                                {index + 1}
                              </button>
                            </td>
                            <td className="max-w-[260px] px-3 py-2 text-[#0f172a]">
                              <span className="block truncate" title={item.itemDescription}>
                                {cell(item.itemDescription)}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-[#0f172a]">{cell(item.partNo)}</td>
                            <td className="px-3 py-2 text-[#0f172a]">{cell(item.impaNo)}</td>
                            <td className="px-3 py-2 text-[#0f172a]">{cell(item.positionNo)}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-[#0f172a]">
                              {cell(item.requiredQuantity)}
                            </td>
                            <td className="px-3 py-2 text-[#0f172a]">{cell(item.uom)}</td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-[11px] font-bold ${pf.cls}`}
                              >
                                <span aria-hidden>{pf.icon}</span>
                                {pf.label}
                              </span>
                            </td>
                          </tr>
                          {expanded ? (
                            <tr className="bg-slate-50/60">
                              <td colSpan={8}>
                                <ItemDetails row={row} />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {model.notImportedColumns.length > 0 ? (
              <p className="mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">
                Not imported (quotation-only columns, not part of an RFQ item):{' '}
                {model.notImportedColumns.join(', ')}
              </p>
            ) : null}
          </section>

          {/* Needs Review — single consolidated list */}
          <section>
            <p className={`${sectionTitleCls} mb-2`}>Needs Review</p>
            {hasIssues ? (
              <div className="divide-y divide-amber-100 rounded-xl border border-amber-200 bg-amber-50/40">
                {issueGroups.map((g) => (
                  <div key={g.id} className="px-4 py-3">
                    <p className="mb-1 flex items-center gap-1.5 text-xs font-bold text-amber-800">
                      <MdWarning className="h-4 w-4" />
                      {g.label}
                    </p>
                    <ul className="ml-5 list-disc space-y-0.5 text-sm text-[#0f172a]">
                      {g.messages.map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <p className="flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                <MdCheckCircle className="h-4 w-4" />
                Nothing needs review.
              </p>
            )}
          </section>

          {model.unrecognizedLabels.length > 0 ? (
            <details className="text-xs text-[hsl(var(--muted-foreground))]">
              <summary className="cursor-pointer select-none font-semibold">
                {model.unrecognizedLabels.length} document label
                {model.unrecognizedLabels.length === 1 ? ' was' : 's were'} not recognized
              </summary>
              <p className="mt-1.5">{model.unrecognizedLabels.join(', ')}</p>
            </details>
          ) : null}
        </div>

        {/* Sticky footer */}
        <div className="flex flex-col gap-3 border-t border-[#0088cc]/15 bg-[#0088cc]/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            {!model.canApply
              ? 'This file cannot be applied until the errors above are resolved.'
              : hasIssues
                ? 'Some fields need review. You can still apply the extracted data.'
                : 'Applying replaces the current form data and items. Attachments are kept.'}
          </p>
          <div className="flex items-center justify-end gap-3">
            <ProcurementActionButton size="modal" onClick={onCancel}>
              Cancel
            </ProcurementActionButton>
            <ProcurementPrimaryButton size="modal" onClick={onConfirm} disabled={!model.canApply}>
              Apply to Form
            </ProcurementPrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
